import { useQuery } from '@tanstack/react-query';
import { CheckCircleIcon } from 'lucide-react';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'sonner';

import { StoryQuestionItem } from '@/components/story-question-item';
import { storyByIdQueryOptions } from '@/shared/queries';
import { Button } from '@/shared/ui/button';
import { Language } from '@/shared/utils';

import { useLanguage } from './language-provider';

const updateFontSize = (fontSize: string) => {
  document.documentElement.style.fontSize = fontSize;
};

export const StoryGuestPage = ({ storyId }: { storyId: string }) => {
  const intl = useIntl();
  const query = useQuery(storyByIdQueryOptions(storyId));
  const { handleLanguageChange, isLanguageCookieSet } = useLanguage();

  useEffect(() => {
    if (query.data?.data.metadata.fontSize) {
      updateFontSize(query.data.data.metadata.fontSize);
    }

    if (query.data?.data.metadata.locale && !isLanguageCookieSet) {
      handleLanguageChange(query.data?.data.metadata.locale as Language);
    }
  }, [
    handleLanguageChange,
    isLanguageCookieSet,
    query.data?.data.metadata.fontSize,
    query.data?.data.metadata.locale,
  ]);

  return (
    <div className="relative pb-20">
      <div className="flex justify-between bg-white px-4 py-6 pb-6 md:px-10">
        <div>
          <p className="text-2xl">
            {intl.formatMessage(
              { id: 'story.guestTitle' },
              {
                storyName: query.data?.data.name,
                b: (msg) => <b>{msg}</b>,
              },
            )}
          </p>

          <p className="text-muted-foreground">
            {intl.formatMessage({ id: 'story.autosave' })}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            onClick={() => {
              /**
               * TODO: Open Success Modal
               */
              toast(
                <div className="flex items-center gap-2">
                  {intl.formatMessage({ id: 'storySubmitSuccess' })}{' '}
                  <CheckCircleIcon color="green" size={16} />
                </div>,
                {
                  duration: 3000,
                },
              );
            }}
          >
            {intl.formatMessage({ id: 'story.finishAndSubmit' })}
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-8 px-4 md:px-10">
        {query.data?.data.questionsCategories.map((category) => (
          <div key={category.id} className="rounded-lg border-2 border-primary p-8">
            <div className="flex justify-between">
              <p className="text-2xl">{category.name}</p>
            </div>

            <div className="mt-4 flex flex-col gap-10">
              {category.questions.map((question) => (
                <StoryQuestionItem
                  guest
                  key={question.id}
                  storyId={storyId}
                  {...question}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
